/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, AlertTriangle, Clock, Users, Boxes, Building2,
} from 'lucide-react';
import { useSubscriptionLifecycle } from '@/hooks/api/useSubscriptionLifecycle';
import { SubscriptionLifecycleItem } from '@/types/superadmin.types';

function formatCurrency(amount: number | null | undefined, currency: string = 'USD'): string {
  if (amount == null) return '-';
  if (currency === 'USD') return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return `${amount.toLocaleString()} ${currency}`;
}

function daysFromNow(dateStr: string): number {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const statusStyles: Record<string, React.CSSProperties> = {
  EXPIRED: { background: 'var(--badge-error-bg)', color: 'var(--badge-error-text)' },
  SUSPENDED: { background: 'var(--badge-warning-bg)', color: 'var(--badge-warning-text)' },
  ACTIVE: { background: 'var(--badge-success-bg)', color: 'var(--badge-success-text)' },
  TRIAL: { background: 'var(--badge-info-bg)', color: 'var(--badge-info-text)' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const style = statusStyles[status] || statusStyles.EXPIRED;
  return (
    <span className="lifecycle-badge" style={style}>
      {status}
    </span>
  );
};

const LifecycleCard: React.FC<{
  item: SubscriptionLifecycleItem;
  variant: 'expired' | 'renewal';
  t: (key: string, opts?: Record<string, unknown>) => string;
  onClick: () => void;
}> = ({ item, variant, t, onClick }) => {
  const days = daysFromNow(item.expiryDate);

  return (
    <div className="lifecycle-card" onClick={onClick}>
      <div className="lifecycle-card-header">
        <div className="lifecycle-card-org">
          <Building2 className="lifecycle-icon-sm" style={{ color: 'var(--color-text-muted)' }} />
          <span className="lifecycle-card-orgname">{item.orgName}</span>
        </div>
        <StatusBadge status={item.status} />
      </div>

      <div className="lifecycle-card-details">
        <div className="lifecycle-card-detail">
          <Users className="lifecycle-icon-xs" />
          <span>{t('subscriptionLifecycle.users', { count: item.userCount })}</span>
        </div>
        {item.modules && (
          <div className="lifecycle-card-detail">
            <Boxes className="lifecycle-icon-xs" />
            <span>{item.modules}</span>
          </div>
        )}
      </div>

      <div className="lifecycle-card-footer">
        <span className="lifecycle-card-amount">
          {formatCurrency(item.amountUsd)}
          {item.amountLocal != null && item.currency !== 'USD' && (
            <span className="lifecycle-card-local"> / {formatCurrency(item.amountLocal, item.currency)}</span>
          )}
        </span>
        <span className="lifecycle-card-date">
          {variant === 'expired'
            ? t('subscriptionLifecycle.expiryDate', { date: new Date(item.expiryDate).toLocaleDateString() })
            : t('subscriptionLifecycle.daysUntilRenewal', { days: Math.abs(days) })}
        </span>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="lifecycle-card lifecycle-skeleton">
    <div className="lifecycle-card-header">
      <div className="skeleton-block skeleton-w-32 skeleton-h-4" />
      <div className="skeleton-block skeleton-w-16 skeleton-h-5 skeleton-rounded" />
    </div>
    <div className="lifecycle-card-details">
      <div className="skeleton-block skeleton-w-20 skeleton-h-3" />
      <div className="skeleton-block skeleton-w-40 skeleton-h-3" />
    </div>
    <div className="lifecycle-card-footer">
      <div className="skeleton-block skeleton-w-16 skeleton-h-4" />
      <div className="skeleton-block skeleton-w-24 skeleton-h-3" />
    </div>
  </div>
);

export default function SubscriptionLifecyclePage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useSubscriptionLifecycle();

  return (
    <div className="lifecycle-page">
      {/* Header */}
      <div className="lifecycle-header">
        <div>
          <h1 className="lifecycle-title">
            <RefreshCw className="lifecycle-icon-lg" />
            {t('subscriptionLifecycle.title')}
          </h1>
        </div>
        <button className="lifecycle-refresh-btn" onClick={() => refetch()}>
          <RefreshCw className="lifecycle-icon-sm" />
        </button>
      </div>

      {isError && (
        <div className="lifecycle-error">
          <AlertTriangle className="lifecycle-icon-md" />
          <span>Failed to load subscription data. Please try again.</span>
        </div>
      )}

      {/* Two-column layout */}
      <div className="lifecycle-grid">
        {/* Left Panel — Expired */}
        <div className="lifecycle-panel">
          <div className="lifecycle-panel-header lifecycle-panel-expired">
            <AlertTriangle className="lifecycle-icon-md" />
            <h2>{t('subscriptionLifecycle.expiredTitle')}</h2>
            {data && <span className="lifecycle-count lifecycle-count-error">{data.expired.length}</span>}
          </div>
          <div className="lifecycle-panel-body">
            {isLoading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : data && data.expired.length > 0 ? (
              data.expired.map((item) => (
                <LifecycleCard
                  key={item.orgId}
                  item={item}
                  variant="expired"
                  t={t}
                  onClick={() => navigate(`/superadmin/organizations/${item.orgId}`)}
                />
              ))
            ) : (
              <div className="lifecycle-empty">
                <Clock className="lifecycle-icon-xl" />
                <p>{t('subscriptionLifecycle.noExpired')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel — Renewals */}
        <div className="lifecycle-panel">
          <div className="lifecycle-panel-header lifecycle-panel-renewals">
            <RefreshCw className="lifecycle-icon-md" />
            <h2>{t('subscriptionLifecycle.renewalsTitle')}</h2>
            {data && <span className="lifecycle-count lifecycle-count-success">{data.renewals.length}</span>}
          </div>
          <div className="lifecycle-panel-body">
            {isLoading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : data && data.renewals.length > 0 ? (
              data.renewals.map((item) => (
                <LifecycleCard
                  key={item.orgId}
                  item={item}
                  variant="renewal"
                  t={t}
                  onClick={() => navigate(`/superadmin/organizations/${item.orgId}`)}
                />
              ))
            ) : (
              <div className="lifecycle-empty">
                <Clock className="lifecycle-icon-xl" />
                <p>{t('subscriptionLifecycle.noRenewals')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* ---- Page Layout ---- */
        .lifecycle-page {
          padding: var(--space-6);
          max-width: 1400px;
          margin: 0 auto;
        }

        /* ---- Icon sizes (replaces Tailwind w-N h-N) ---- */
        .lifecycle-icon-xs { width: 14px; height: 14px; }
        .lifecycle-icon-sm { width: 16px; height: 16px; }
        .lifecycle-icon-md { width: 20px; height: 20px; }
        .lifecycle-icon-lg { width: 24px; height: 24px; }
        .lifecycle-icon-xl { width: 32px; height: 32px; }

        /* ---- Header ---- */
        .lifecycle-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-6);
        }

        .lifecycle-title {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-3xl);
          font-weight: var(--primitive-weight-bold);
          color: var(--color-text-primary);
          font-family: var(--font-heading);
        }

        .lifecycle-refresh-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-subtle);
          background: var(--color-bg-secondary);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-default);
        }
        .lifecycle-refresh-btn:hover {
          background: var(--color-bg-hover);
          color: var(--color-text-primary);
        }

        /* ---- Error banner ---- */
        .lifecycle-error {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-4);
          margin-bottom: var(--space-4);
          border-radius: var(--radius-lg);
          background: var(--color-error-light);
          color: var(--color-error);
          font-size: var(--text-md);
        }

        /* ---- Two-column grid ---- */
        .lifecycle-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-6);
        }

        @media (max-width: 768px) {
          .lifecycle-grid {
            grid-template-columns: 1fr;
          }
        }

        /* ---- Panels ---- */
        .lifecycle-panel {
          border: 1px solid var(--card-border);
          border-radius: var(--card-radius);
          overflow: hidden;
          background: var(--card-bg);
          box-shadow: var(--card-shadow);
        }

        .lifecycle-panel-header {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-4);
          font-weight: var(--primitive-weight-semibold);
          font-size: var(--text-md);
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .lifecycle-panel-header h2 {
          flex: 1;
          font-size: inherit;
          font-weight: inherit;
          margin: 0;
        }

        .lifecycle-panel-expired {
          background: var(--color-error-light);
          color: var(--color-error);
          border-top: 3px solid var(--color-error);
        }

        .lifecycle-panel-renewals {
          background: var(--color-success-light);
          color: var(--color-success);
          border-top: 3px solid var(--color-success);
        }

        /* ---- Count badge ---- */
        .lifecycle-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 var(--space-1-5);
          border-radius: var(--radius-full);
          font-size: var(--text-xs);
          font-weight: var(--primitive-weight-bold);
          color: var(--color-text-on-brand);
        }
        .lifecycle-count-error {
          background: var(--color-error);
        }
        .lifecycle-count-success {
          background: var(--color-success);
        }

        /* ---- Panel body ---- */
        .lifecycle-panel-body {
          padding: var(--space-3);
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          max-height: 600px;
          overflow-y: auto;
        }

        /* ---- Cards ---- */
        .lifecycle-card {
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-lg);
          padding: var(--space-3) var(--space-4);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-default);
          background: var(--color-bg-secondary);
        }
        .lifecycle-card:hover {
          border-color: var(--color-brand-primary);
          box-shadow: var(--card-shadow);
        }

        .lifecycle-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-2);
        }

        .lifecycle-card-org {
          display: flex;
          align-items: center;
          gap: var(--space-1-5);
        }

        .lifecycle-card-orgname {
          font-weight: var(--primitive-weight-semibold);
          font-size: var(--text-md);
          color: var(--color-text-primary);
        }

        .lifecycle-card-details {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
          margin-bottom: var(--space-2);
        }

        .lifecycle-card-detail {
          display: flex;
          align-items: center;
          gap: var(--space-1-5);
          font-size: var(--text-sm);
          color: var(--color-text-muted);
        }

        .lifecycle-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: var(--space-2);
          border-top: 1px solid var(--color-border-subtle);
        }

        .lifecycle-card-amount {
          font-weight: var(--primitive-weight-semibold);
          font-size: var(--text-md);
          color: var(--color-text-primary);
        }

        .lifecycle-card-local {
          font-weight: var(--primitive-weight-regular);
          font-size: var(--text-xs);
          color: var(--color-text-muted);
        }

        .lifecycle-card-date {
          font-size: var(--text-xs);
          color: var(--color-text-muted);
        }

        /* ---- Status badge ---- */
        .lifecycle-badge {
          display: inline-flex;
          align-items: center;
          padding: var(--space-0-5) var(--space-2);
          border-radius: var(--radius-full);
          font-size: var(--text-xs);
          font-weight: var(--primitive-weight-semibold);
        }

        /* ---- Empty state ---- */
        .lifecycle-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-8) var(--space-4);
          color: var(--color-text-muted);
          text-align: center;
        }
        .lifecycle-empty p {
          font-size: var(--text-md);
          margin: 0;
        }

        /* ---- Skeleton loading ---- */
        .lifecycle-skeleton {
          pointer-events: none;
        }

        .skeleton-block {
          border-radius: var(--radius-sm);
          background: var(--color-bg-hover);
          animation: lifecycle-pulse 1.5s var(--ease-default) infinite;
        }

        .skeleton-rounded { border-radius: var(--radius-full); }
        .skeleton-w-16 { width: 64px; }
        .skeleton-w-20 { width: 80px; }
        .skeleton-w-24 { width: 96px; }
        .skeleton-w-32 { width: 128px; }
        .skeleton-w-40 { width: 160px; }
        .skeleton-h-3 { height: 12px; }
        .skeleton-h-4 { height: 16px; }
        .skeleton-h-5 { height: 20px; }

        @keyframes lifecycle-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
